import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  HiOutlineCheckCircle,
  HiOutlineDocumentText,
  HiOutlinePlus,
  HiOutlineTrash,
} from "react-icons/hi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import FilePreviewModal, {
  detectPreviewKind,
  type FilePreviewKind,
} from "components/FilePreviewModal";

import {
  getSurveillanceStatusContext,
  saveSurveillanceNoteFields,
  generateSurveillanceNotePdf,
  sendSurveillanceNoteToClient,
  type SurveillanceStatusContext,
  type SurveillanceConflictingMarkEntry,
} from "../../api/surveillance";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const SURVEILLANCE_NOTE_OF_SURVEILLANCE_FORM_ID =
  "surveillance-note-of-surveillance-form";

const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const INPUT_CLASS =
  "w-full h-9 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const READONLY_INPUT_CLASS =
  "w-full h-9 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px] cursor-not-allowed box-border";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

function getMonthYearFromIso(iso: string): { month: string; year: string } {
  if (!iso) return { month: "", year: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { month: "", year: "" };
  return {
    month: MONTH_NAMES[d.getMonth()] ?? "",
    year: String(d.getFullYear()),
  };
}

const CONFLICT_STATUS_OPTIONS = [
  "Registered",
  "Refused",
  "Objected",
  "Abandoned",
  "Pending",
  "Withdrawn",
] as const;

type EditableConflictRow = SurveillanceConflictingMarkEntry & { rowKey: string };

let rowCounter = 0;
function makeRowKey() {
  rowCounter += 1;
  return `cr-${rowCounter}-${Date.now()}`;
}

function makeEmptyRow(conflictNo: number): EditableConflictRow {
  return {
    rowKey: makeRowKey(),
    conflictNo,
    mark: "",
    applicationNumber: "",
    applicant: "",
    applicantPlace: "",
    status: "",
  };
}

function toApiRows(rows: EditableConflictRow[]): SurveillanceConflictingMarkEntry[] {
  return rows.map(({ rowKey: _rk, ...rest }) => rest);
}

function fromApiRows(rows: SurveillanceConflictingMarkEntry[]): EditableConflictRow[] {
  return rows.map((r) => ({ ...r, rowKey: makeRowKey() }));
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type PdfMeta = { key: string; url: string; filename: string };

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function SurveillanceNoteOfSurveillanceView({
  leadId,
  onSuccess,
}: Props) {
  const [ctx, setCtx] = useState<SurveillanceStatusContext | null>(null);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const [reportDate, setReportDate] = useState<string>("");
  const [conflictRows, setConflictRows] = useState<EditableConflictRow[]>([]);
  const [pdf, setPdf] = useState<PdfMeta | null>(null);
  const [pdfIsStale, setPdfIsStale] = useState(false);
  const [noteSentAt, setNoteSentAt] = useState<string | null>(null);
  const [previewState, setPreviewState] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
  } | null>(null);

  const initializedForLead = useRef<string | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setCtx(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getSurveillanceStatusContext(leadId)
      .then((data) => {
        if (cancelled) return;
        setCtx(data);
        if (initializedForLead.current !== leadId) {
          initializedForLead.current = leadId;
          setReportDate(data.reportDate ?? "");
          setConflictRows(
            data.conflictingMarks.length
              ? fromApiRows(data.conflictingMarks)
              : []
          );
        }
        setPdf(data.pdf);
        setPdfIsStale(data.pdfIsStale);
        setNoteSentAt(data.noteSentAt);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load note data"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const applyContextUpdate = (data: SurveillanceStatusContext) => {
    setCtx(data);
    setPdf(data.pdf);
    setPdfIsStale(data.pdfIsStale);
    setNoteSentAt(data.noteSentAt);
  };

  const addConflictRow = () => {
    setConflictRows((prev) => [
      ...prev,
      makeEmptyRow(prev.length + 1),
    ]);
  };

  const removeConflictRow = (rowKey: string) => {
    setConflictRows((prev) => {
      const next = prev
        .filter((r) => r.rowKey !== rowKey)
        .map((r, i) => ({ ...r, conflictNo: i + 1 }));
      return next;
    });
  };

  const updateConflictField = (
    rowKey: string,
    field: keyof Omit<EditableConflictRow, "rowKey" | "conflictNo">,
    value: string
  ) => {
    setConflictRows((prev) =>
      prev.map((r) => (r.rowKey === rowKey ? { ...r, [field]: value } : r))
    );
  };

  const handleSaveInPage = async () => {
    if (!leadId || saving) return;
    const incompleteRow = conflictRows.find(
      (r) => !r.mark.trim() || !r.applicationNumber.trim() || !r.applicant.trim() || !r.applicantPlace.trim() || !r.status
    );
    if (incompleteRow) {
      toast.error("Please fill in all fields for each conflicting mark row.");
      return;
    }
    setSaving(true);
    try {
      const data = await saveSurveillanceNoteFields(leadId, {
        reportDate: reportDate || null,
        conflictingMarks: conflictRows.length ? toApiRows(conflictRows) : null,
      });
      applyContextUpdate(data);
      toast.success("Note fields saved.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save note fields");
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async (force = false) => {
    if (!leadId || generatingPdf) return;
    setGeneratingPdf(true);
    try {
      const data = await generateSurveillanceNotePdf(leadId, force);
      setPdf({ key: data.key, url: data.url, filename: data.filename });
      setPdfIsStale(false);
      toast.success("PDF generated successfully.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!leadId || sendingWhatsApp) return;
    if (!pdf) {
      toast.error("Generate the PDF first before sending.");
      return;
    }
    setSendingWhatsApp(true);
    try {
      const result = await sendSurveillanceNoteToClient(leadId, {
        sendWhatsApp: true,
        sendEmail: false,
      });
      setNoteSentAt(new Date().toISOString());
      if (result.whatsAppLink) {
        window.open(result.whatsAppLink, "_blank", "noopener,noreferrer");
      }
      toast.success("WhatsApp message sent.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send WhatsApp");
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSendEmail = async () => {
    if (!leadId || sendingEmail) return;
    if (!pdf) {
      toast.error("Generate the PDF first before sending.");
      return;
    }
    setSendingEmail(true);
    try {
      await sendSurveillanceNoteToClient(leadId, {
        sendWhatsApp: false,
        sendEmail: true,
      });
      setNoteSentAt(new Date().toISOString());
      toast.success("Email sent to client.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!pdf) return;
    try {
      const result = await downloadFileFromUrlWithFallback(pdf.url, pdf.filename);
      if (result === "local") {
        toast.success("File saved to your device.");
      } else {
        toast.info("Opened in a new tab. Use Save As to store it locally.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download");
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    const incompleteRow = conflictRows.find(
      (r) => !r.mark.trim() || !r.applicationNumber.trim() || !r.applicant.trim() || !r.applicantPlace.trim() || !r.status
    );
    if (incompleteRow) {
      toast.error("Please fill in all fields for each conflicting mark row.");
      return;
    }
    setSaving(true);
    try {
      const data = await saveSurveillanceNoteFields(leadId, {
        reportDate: reportDate || null,
        conflictingMarks: conflictRows.length ? toApiRows(conflictRows) : null,
      });
      applyContextUpdate(data);
      toast.success("Note of Surveillance saved.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-5 w-full py-12" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-sm text-[#6D6D6D]">
          Please complete the Discussion steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-5 w-full py-12" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  return (
    <form
      id={SURVEILLANCE_NOTE_OF_SURVEILLANCE_FORM_ID}
      onSubmit={handleFormSubmit}
      className="flex flex-col gap-5 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Header */}
      <div className="flex flex-col gap-1.5">
        <p
          className="font-semibold text-[#101111]"
          style={{ fontSize: "14px", lineHeight: "100%", letterSpacing: "0.01em" }}
        >
          Note of Surveillance to Client
        </p>
        <p
          className="font-normal text-[#7D7D7D]"
          style={{ fontSize: "12px", lineHeight: "20px", letterSpacing: "0.04em" }}
        >
          Review the surveillance summary, set the report date, and proceed.
        </p>
      </div>

      {/* Summary grid — matches Figma layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        {/* Addressee */}
        <div className="flex flex-col gap-1.5">
          <label className={LABEL_CLASS} style={LABEL_FEATURES} htmlFor="sv-note-addressee">
            Addressee
          </label>
          <input
            id="sv-note-addressee"
            type="text"
            className={READONLY_INPUT_CLASS}
            placeholder="Text Here"
            value={ctx?.addressee ?? ""}
            readOnly
            tabIndex={-1}
          />
        </div>

        {/* Form of Address */}
        <div className="flex flex-col gap-1.5">
          <label className={LABEL_CLASS} style={LABEL_FEATURES} htmlFor="sv-note-form-of-address">
            Form of Address
          </label>
          <input
            id="sv-note-form-of-address"
            type="text"
            className={READONLY_INPUT_CLASS}
            placeholder="Text Here"
            value={ctx?.formOfAddress ?? ""}
            readOnly
            tabIndex={-1}
          />
        </div>

        {/* Mark */}
        <div className="flex flex-col gap-1.5">
          <label className={LABEL_CLASS} style={LABEL_FEATURES} htmlFor="sv-note-mark">
            Mark
          </label>
          <input
            id="sv-note-mark"
            type="text"
            className={READONLY_INPUT_CLASS}
            placeholder="Text Here"
            value={ctx?.marks ?? ""}
            readOnly
            tabIndex={-1}
          />
        </div>

        {/* Selected Class */}
        <div className="flex flex-col gap-1.5">
          <label className={LABEL_CLASS} style={LABEL_FEATURES} htmlFor="sv-note-selected-class">
            Selected Class
          </label>
          <input
            id="sv-note-selected-class"
            type="text"
            className={READONLY_INPUT_CLASS}
            placeholder="Text Here"
            value={ctx?.selectedClasses ?? ""}
            readOnly
            tabIndex={-1}
          />
        </div>

        {/* List of Conflicting Marks (derived from rows below) */}
        <div className="flex flex-col gap-1.5">
          <label className={LABEL_CLASS} style={LABEL_FEATURES} htmlFor="sv-note-list-conflicts">
            List of Conflicting Marks
          </label>
          <input
            id="sv-note-list-conflicts"
            type="text"
            className={READONLY_INPUT_CLASS}
            placeholder="Text Here"
            value={conflictRows.map((r) => r.mark).filter(Boolean).join(", ")}
            readOnly
            tabIndex={-1}
          />
        </div>

        {/* No of Conflicting Marks (derived count) */}
        <div className="flex flex-col gap-1.5">
          <label className={LABEL_CLASS} style={LABEL_FEATURES} htmlFor="sv-note-no-conflicts">
            No of Conflicting Marks
          </label>
          <input
            id="sv-note-no-conflicts"
            type="text"
            className={READONLY_INPUT_CLASS}
            placeholder="Text Here"
            value={conflictRows.length ? String(conflictRows.length) : ""}
            readOnly
            tabIndex={-1}
          />
        </div>

        {/* Date of Surveillance report to client (editable) */}
        <div className="flex flex-col gap-1.5">
          <label className={LABEL_CLASS} style={LABEL_FEATURES} htmlFor="sv-report-date">
            Date of Surveillance report to client
          </label>
          <input
            id="sv-report-date"
            type="date"
            className={INPUT_CLASS}
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
          />
        </div>

        {/* Month of Surveillance report (derived) */}
        <div className="flex flex-col gap-1.5">
          <label className={LABEL_CLASS} style={LABEL_FEATURES} htmlFor="sv-note-month">
            Month of Surveillance report
          </label>
          <input
            id="sv-note-month"
            type="text"
            className={READONLY_INPUT_CLASS}
            placeholder="Text Here"
            value={getMonthYearFromIso(reportDate).month}
            readOnly
            tabIndex={-1}
          />
        </div>

        {/* Year of Surveillance report (derived) */}
        <div className="flex flex-col gap-1.5">
          <label className={LABEL_CLASS} style={LABEL_FEATURES} htmlFor="sv-note-year">
            Year of Surveillance report
          </label>
          <input
            id="sv-note-year"
            type="text"
            className={READONLY_INPUT_CLASS}
            placeholder="Text Here"
            value={getMonthYearFromIso(reportDate).year}
            readOnly
            tabIndex={-1}
          />
        </div>
      </div>

      {/* Conflicting marks table */}
      <div className="flex flex-col gap-3 pt-1 border-t border-[#EEEEEE]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <p className="font-medium text-[#222222]" style={{ fontSize: "12px", ...LABEL_FEATURES }}>
              Conflicting Mark Details
              {conflictRows.length > 0 && (
                <span className="ml-1.5 text-[#6D6D6D] font-normal">({conflictRows.length})</span>
              )}
            </p>
            <p className="text-[11px] text-[#6D6D6D]" style={LABEL_FEATURES}>
              Capture each conflicting mark — these populate the report PDF.
            </p>
          </div>
          <button
            type="button"
            onClick={addConflictRow}
            className="flex items-center gap-1.5 px-3 py-1.5 h-8 text-[12px] font-medium text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] transition-colors"
          >
            <HiOutlinePlus className="w-3.5 h-3.5" strokeWidth={2} />
            Add Row
          </button>
        </div>

        {conflictRows.length === 0 ? (
          <div className="flex items-center justify-center py-8 rounded-[8px] border border-dashed border-[#EEEEEE] text-[12px] text-[#6D6D6D]">
            No conflicting marks added yet. Click "Add Row" to add one.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block w-full overflow-x-auto">
              <table className="w-full border-separate border-spacing-y-1.5 min-w-[820px]">
                <thead>
                  <tr>
                    {["#", "Mark", "App. No.", "Applicant", "Place", "Status", ""].map((h, i) => (
                      <th
                        key={i}
                        className="text-left text-[11px] font-normal text-[#6D6D6D] px-1 pb-1"
                        style={LABEL_FEATURES}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {conflictRows.map((row) => (
                    <tr key={row.rowKey} className="align-top">
                      <td className="px-1 w-8">
                        <div className="flex items-center h-9 text-[12px] text-[#6D6D6D] font-medium">
                          {row.conflictNo}
                        </div>
                      </td>
                      <td className="px-1 w-[150px]">
                        <input
                          className={INPUT_CLASS}
                          placeholder="Mark name"
                          value={row.mark}
                          onChange={(e) => updateConflictField(row.rowKey, "mark", e.target.value)}
                        />
                      </td>
                      <td className="px-1 w-[120px]">
                        <input
                          className={INPUT_CLASS}
                          placeholder="App. no."
                          value={row.applicationNumber}
                          onChange={(e) => updateConflictField(row.rowKey, "applicationNumber", e.target.value)}
                        />
                      </td>
                      <td className="px-1 w-[180px]">
                        <input
                          className={INPUT_CLASS}
                          placeholder="Applicant name"
                          value={row.applicant}
                          onChange={(e) => updateConflictField(row.rowKey, "applicant", e.target.value)}
                        />
                      </td>
                      <td className="px-1 w-[120px]">
                        <input
                          className={INPUT_CLASS}
                          placeholder="Place"
                          value={row.applicantPlace}
                          onChange={(e) => updateConflictField(row.rowKey, "applicantPlace", e.target.value)}
                        />
                      </td>
                      <td className="px-1 w-[140px]">
                        <Select
                          value={row.status}
                          onValueChange={(val) => updateConflictField(row.rowKey, "status", val)}
                        >
                          <SelectTrigger className="h-9 text-[12px] border-[#EEEEEE] focus:ring-[#222]">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONFLICT_STATUS_OPTIONS.map((opt) => (
                              <SelectItem key={opt} value={opt} className="text-[12px]">
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-1 w-9">
                        <button
                          type="button"
                          onClick={() => removeConflictRow(row.rowKey)}
                          className="flex items-center justify-center w-9 h-9 rounded-[6px] text-[#6D6D6D] hover:text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                          aria-label="Remove row"
                        >
                          <HiOutlineTrash className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: stacked cards */}
            <div className="md:hidden flex flex-col gap-3">
              {conflictRows.map((row) => (
                <div
                  key={row.rowKey}
                  className="flex flex-col gap-3 p-4 rounded-[10px] border border-[#EEEEEE] bg-white"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] font-semibold text-[#222222]">
                      Conflict #{row.conflictNo}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeConflictRow(row.rowKey)}
                      className="text-[#6D6D6D] hover:text-[#EF4444] p-1 transition-colors"
                      aria-label="Remove"
                    >
                      <HiOutlineTrash className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-2.5">
                    {(
                      [
                        { label: "Mark", field: "mark", placeholder: "Mark name" },
                        { label: "App. No.", field: "applicationNumber", placeholder: "Application number" },
                        { label: "Applicant", field: "applicant", placeholder: "Applicant name" },
                        { label: "Place", field: "applicantPlace", placeholder: "Place" },
                      ] as const
                    ).map(({ label, field, placeholder }) => (
                      <div key={field} className="flex flex-col gap-1">
                        <label className={LABEL_CLASS} style={LABEL_FEATURES}>{label}</label>
                        <input
                          className={INPUT_CLASS}
                          placeholder={placeholder}
                          value={row[field]}
                          onChange={(e) => updateConflictField(row.rowKey, field, e.target.value)}
                        />
                      </div>
                    ))}
                    <div className="flex flex-col gap-1">
                      <label className={LABEL_CLASS} style={LABEL_FEATURES}>Status</label>
                      <Select
                        value={row.status}
                        onValueChange={(val) => updateConflictField(row.rowKey, "status", val)}
                      >
                        <SelectTrigger className="h-9 text-[12px] border-[#EEEEEE] focus:ring-[#222]">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {CONFLICT_STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt} className="text-[12px]">
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* In-page save button */}
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={handleSaveInPage}
            disabled={saving}
            className="px-4 py-2 h-9 text-[12px] font-semibold text-[#222] border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Generate PDF section */}
      <div className="flex flex-col gap-3 pt-1 border-t border-[#EEEEEE]">
        <p className="font-medium text-[#222222]" style={{ fontSize: "12px", ...LABEL_FEATURES }}>
          Note of Surveillance PDF
        </p>

        {pdf ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 px-4 py-3 rounded-[8px] border border-[#D1FAE5] bg-[#F0FDF4]">
              <HiOutlineDocumentText className="w-5 h-5 text-[#16A34A] flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-[#16A34A] truncate" title={pdf.filename}>
                  {pdf.filename}
                </p>
                {pdfIsStale && (
                  <p className="text-[11px] text-[#F59E0B]">
                    Inputs changed — regenerate to update the PDF.
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() =>
                  setPreviewState({
                    url: pdf.url,
                    name: pdf.filename,
                    kind: detectPreviewKind(pdf.filename),
                  })
                }
                className="px-3 h-8 text-[12px] font-medium text-[#2E3037] border border-[#D5D3E0] rounded-[6px] bg-white hover:bg-gray-50"
              >
                Preview
              </button>
              <button
                type="button"
                onClick={handleDownloadPdf}
                className="px-3 h-8 text-[12px] font-medium text-[#2E3037] border border-[#D5D3E0] rounded-[6px] bg-white hover:bg-gray-50"
              >
                Download
              </button>
              <button
                type="button"
                onClick={() => void handleGeneratePdf(true)}
                disabled={generatingPdf}
                className="px-3 h-8 text-[12px] font-medium text-[#6D6D6D] border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPdf ? "Regenerating…" : "Regenerate"}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => void handleGeneratePdf(false)}
            disabled={generatingPdf}
            className="self-start flex items-center gap-2 px-4 py-2 h-9 text-[12px] font-semibold text-white rounded-[6px] bg-[#222] hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <HiOutlineDocumentText className="w-4 h-4" strokeWidth={1.5} />
            {generatingPdf ? "Generating…" : "Generate PDF"}
          </button>
        )}
      </div>

      {/* Send to client section */}
      <div className="flex flex-col gap-3 pt-1 border-t border-[#EEEEEE]">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="font-medium text-[#222222]" style={{ fontSize: "12px", ...LABEL_FEATURES }}>
            Send to Client
          </p>
          {noteSentAt && (
            <div className="flex items-center gap-1.5 text-[11px] text-[#16A34A]">
              <HiOutlineCheckCircle className="w-4 h-4" strokeWidth={1.5} />
              <span>Sent on {formatDateTime(noteSentAt)}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleSendWhatsApp}
            disabled={!pdf || sendingWhatsApp}
            className="flex items-center gap-2 px-4 py-2 h-9 text-[12px] font-semibold text-white rounded-[6px] bg-[#222] hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {sendingWhatsApp
              ? "Sending…"
              : noteSentAt
              ? "Resend via WhatsApp"
              : "Send via WhatsApp"}
          </button>
          <button
            type="button"
            onClick={handleSendEmail}
            disabled={!pdf || sendingEmail}
            className="flex items-center gap-2 px-4 py-2 h-9 text-[12px] font-semibold text-[#222222] border border-[#EEEEEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {sendingEmail
              ? "Sending…"
              : noteSentAt
              ? "Resend via Email"
              : "Send via Email"}
          </button>
        </div>

        {!pdf && (
          <p className="text-[11px] text-[#6D6D6D]">
            Generate the PDF before sending.
          </p>
        )}
      </div>

      <FilePreviewModal
        open={!!previewState}
        onClose={() => setPreviewState(null)}
        name={previewState?.name ?? ""}
        url={previewState?.url}
        kind={previewState?.kind}
      />
    </form>
  );
}
